import { HttpEvent, HttpEventType } from '@angular/common/http'
import { AfterViewInit, Component, inject, LOCALE_ID, NgZone, ViewChild } from '@angular/core'
import { NgForm, FormsModule } from '@angular/forms'

import { concat, EMPTY, Observable, of } from 'rxjs'
import { catchError, mergeMap, tap, timeout } from 'rxjs/operators'

import { MessageService, PrimeTemplate } from 'primeng/api'
import { Button } from 'primeng/button'
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog'
import { FileProgressEvent, FileUpload, FileUploadHandlerEvent } from 'primeng/fileupload'

import { Message } from 'primeng/message'
import { ProgressSpinner } from 'primeng/progressspinner'
import { ArchiveDocumentsApi } from '@app/apis/archive-documents.api'
import type { ArchiveDocumentDetail } from '@app/generated/api/model'

@Component({
  templateUrl: 'file-upload-modal.component.html',
  imports: [
    FormsModule,
    Message,
    FileUpload,
    PrimeTemplate,
    ProgressSpinner,
    Button,
  ],
})

export class FileUploadModalComponent implements AfterViewInit {
  private readonly messageService = inject(MessageService)
  private readonly archiveDocumentsApi = inject(ArchiveDocumentsApi)
  public readonly ref = inject(DynamicDialogRef)
  public readonly zone = inject(NgZone)
  public readonly config = inject(DynamicDialogConfig)
  public readonly locale = inject(LOCALE_ID)

  // whether the form is currently uploading
  uploading = false
  // list of error messages to display above the form
  errorMessage: string = ''
  // how many files have been processed, either successes or fails
  numProcessed = 0
  // list of files that failed to upload
  badFiles: File[] = []
  // list of files that had success to upload
  goodFiles: File[] = []
  // stop semaphore, used to stop uploads if the modal is closed
  stopProcessing = false

  @ViewChild('theFileUpload') public fileUploadControl!: FileUpload
  @ViewChild('f', { static: true }) public detailForm!: NgForm

  ngAfterViewInit () {
    setTimeout(() => {
      // get the list of files from the main (sidebar) component and launch the upload automatically
      const event = { target: { files: this.config.data?.files } }
      this.fileUploadControl.onFileSelect(event)
    })
  }

  /**
   * Function called for each individual file/upload
   */
  private createRequest = (file: File): Observable<ArchiveDocumentDetail> => {
    // received stop signal, don't send any more documents
    if (this.stopProcessing) return EMPTY

    return this.archiveDocumentsApi.uploadFile(file)
      .pipe(
        timeout(1000 * 60 * 5),
        tap({
          next: () => {
            this.goodFiles.push(file)
          },
          error: () => {
            this.badFiles.push(file)
          },
        }),
        catchError(error => of(error)),
        tap(() => {
          // increment progress bar
          this.numProcessed += 1
          const progress = this.numProcessed * 100 / this.fileUploadControl.files.length
          this.fileUploadControl.onProgress.emit(fileProgressEvent(progress))
          // if this was the last file trigger the cleanup function
          if (progress > 99.99) {
            this.finishedUpload(this.badFiles, this.goodFiles)
          }
        }),
      )
  }

  /**
   * Custom upload handler for the form.
   * Gathers all files to be uploaded and launches them with a 'process pool' max number of simultaneous uploads.
   */
  doUpload (event: FileUploadHandlerEvent) {
    if (!event.files.length) {
      return
    }

    const maxProcs = 2
    this.uploading = true
    this.fileUploadControl.onProgress.emit(fileProgressEvent(0))
    this.numProcessed = 0
    this.goodFiles = []
    this.badFiles = []

    const theFiles: Observable<File> = concat(event.files)
    // launch the downloads with no more than `maxProcs` simultaneous
    theFiles.pipe(
      mergeMap(
        file => this.createRequest(file),
        maxProcs,
      ),
    ).subscribe()
  }

  /**
   * Function called when all files have finished processing.
   * Will present an error message if there were any errors otherwise close the modal.
   */
  finishedUpload (badFiles: File[], goodFiles: File[]) {
    this.zone.run(() => { this.uploading = false })
    if (badFiles.length === 0) {
      this.ref.close()
      this.messageService.add({ severity: 'success', life: 2000, summary: '', detail: `${goodFiles.length} documents uploaded` })
      return
    }

    this.errorMessage = $localize`:@@file-upload-modal-some-bad-files:Some files were not uploaded correctly, you can try to re-upload them now.`
    this.fileUploadControl.files = badFiles
  }

  /**
   * callback to update the progress bar
   */
  onProgress (event: FileProgressEvent) {
    this.fileUploadControl.progress = event.progress
  }

  onClose () {
    // signal to stop any uploads that are in the queue
    this.stopProcessing = true
    this.ref.close()
  }
}

function fileProgressEvent (progress: number): FileProgressEvent {
  const originalEvent: HttpEvent<unknown> = { type: HttpEventType.User }
  return { originalEvent, progress }
}
